import React, { useState } from 'react';

const CsvExport = ({ looks, season }) => {
  const [validation, setValidation] = useState(null);
  const [showPanel, setShowPanel] = useState(false);

  const seasonLooks = (looks || []).filter(l => l.season === season);

  // A collage is "look-only" if all its photos reference existing looks or database poses
  // (no direct student photo placement needed, so face/eye data is optional)
  const isLookOnlyCollage = (look) => {
    if (look.look_type !== 'collage') return false;
    const photos = look.collage_photos || [];
    if (photos.length === 0) return false;
    return photos.every(p => p.look_ref || (p.source_type === 'database' && p.pose_id));
  };

  const validate = () => {
    const errors = [];
    const warnings = [];

    if (seasonLooks.length === 0) {
      errors.push(`No looks found for season "${season || '(none)'}".`);
      return { errors, warnings };
    }

    seasonLooks.forEach(look => {
      const prefix = `Look "${look.name || '(unnamed)'}"`;

      if (!look.name) errors.push(`${prefix}: Missing name`);
      if (!look.canvas_width || !look.canvas_height) errors.push(`${prefix}: Missing canvas dimensions`);
      if (look.look_type === 'single' && !look.source_if_3 && !look.source_if_2 && !look.source_if_1) {
        errors.push(`${prefix}: Single look has no pose source (source_if_3, source_if_2, or source_if_1)`);
      }

      if (!isLookOnlyCollage(look)) {
        if (look.eyes_y == null && look.face_height == null && look.center_x == null) {
          if (look.look_type === 'single') {
            errors.push(`${prefix}: Missing face placement data (eyes_y, face_height, center_x)`);
          }
        }
      }

      if (look.look_type === 'collage' && !look.canvas_filepath) {
        warnings.push(`${prefix}: Collage has no canvas background S3 path`);
      }
      if (look.look_type === 'collage' && !look.canvas_filename) {
        warnings.push(`${prefix}: Collage has no canvas background filename`);
      }
      if (look.overlay_s3_path && !look.overlay_s3_path.startsWith('s3://') && !look.overlay_s3_path.startsWith('favorites_designer/')) {
        warnings.push(`${prefix}: Overlay S3 path looks malformed`);
      }
      if (look.look_type === 'collage' && (!look.layer_order || look.layer_order.length === 0)) {
        warnings.push(`${prefix}: No layer order defined — default order will be used`);
      }

      // Validate collage photos have required data
      if (look.look_type === 'collage') {
        (look.collage_photos || []).forEach((photo, pi) => {
          const pp = `${prefix} Photo ${pi + 1}`;
          if (!photo.look_ref && !photo.pose_id) {
            warnings.push(`${pp}: No look or pose selected`);
          }
          if (photo.x == null || photo.y == null) {
            warnings.push(`${pp}: Missing position (x/y)`);
          }
          if (photo.width == null || photo.height == null) {
            warnings.push(`${pp}: Missing dimensions (width/height)`);
          }
          if (photo.source_type === 'database' && !photo.pose_s3_key) {
            warnings.push(`${pp}: Database pose missing S3 key`);
          }
        });
      }

      (look.text_fields || []).forEach((tf, i) => {
        const fPrefix = `${prefix} Field ${i + 1}`;
        if (tf.bound_x_left != null && tf.bound_x_right != null && look.canvas_width && tf.font_size_max) {
          const boundWidthPx = (tf.bound_x_right - tf.bound_x_left) * look.canvas_width;
          if (boundWidthPx < tf.font_size_max * 3) {
            warnings.push(`${fPrefix}: Bounding box may be too narrow for font size`);
          }
        }
      });
    });

    return { errors, warnings };
  };

  const handleGenerate = () => {
    const result = validate();
    setValidation(result);
    setShowPanel(true);
  };

  // Format decimal as percentage string: 0.2604 → "26.04%"
  const fmtPct = (val) => {
    if (val == null) return '';
    return `${(val * 100).toFixed(2)}%`;
  };

  const generateCsv = () => {
    if (seasonLooks.length === 0) return;

    const maxPhotos = Math.max(2, ...seasonLooks.map(l => (l.collage_photos || []).length));
    const maxFields = Math.max(2, ...seasonLooks.map(l => (l.text_fields || []).length));

    // === BUILD HEADER ===
    const headers = [
      // Look identity
      'id', 'season', 'pose', 'type', 'look_only_collage',
      // Source pose selection (singles)
      'source_if_3', 'source_if_2', 'source_if_1',
      // Canvas dimensions
      'canvas_width', 'canvas_height', 'canvas_dpi',
      'canvas_filepath', 'canvas_filename',
      'eye_midpoint_y_canvas', 'face_target_height', 'face_center_x_location',
      'rotation', 'rotation_order', 'rotation_method',
    ];

    // Photo columns (dynamic)
    for (let n = 1; n <= maxPhotos; n++) {
      headers.push(
        `photo_${n}_id`, `photo_${n}_id_type`, `photo_${n}_id_lookup_column`,
        `photo_${n}_fit_mode`,
        `photo_${n}_width`, `photo_${n}_height`,
        `photo_${n}_x_topleft`, `photo_${n}_y_topleft`
      );
    }

    // Overlay
    headers.push('overlay_path', 'overlay_filename', 'overlay_composite_operation', 'overlay_affects_layers_below');

    // Text field columns (dynamic)
    for (let n = 1; n <= maxFields; n++) {
      headers.push(
        `field_${n}`,
        `field_${n}_font_filepath`, `field_${n}_font`, `field_${n}_font_color`,
        `field_${n}_font_size_min`, `field_${n}_font_size_max`,
        `field_${n}_center_x`, `field_${n}_center_y`,
        `field_${n}_bound_x_left`, `field_${n}_bound_x_right`,
        `field_${n}_sizing_rule`
      );
    }

    // === BUILD ROWS ===
    const rows = seasonLooks.map(look => {
      const photos = look.collage_photos || [];
      const fields = look.text_fields || [];
      const isCollage = look.look_type === 'collage';
      const hasRotation = look.rotation && look.rotation !== 0;

      const cw = look.canvas_width;
      const ch = look.canvas_height;

      const row = [
        look.name || '',
        look.season || '',
        look.pose_number || '',
        look.look_type || '',
        isCollage && isLookOnlyCollage(look) ? 'true' : '',
        look.source_if_3 || '',
        look.source_if_2 || '',
        look.source_if_1 || '',
        cw ? `${cw}px` : '',
        ch ? `${ch}px` : '',
        look.canvas_dpi ?? '',
        isCollage ? (look.canvas_filepath || '') : 'N/A',
        isCollage ? (look.canvas_filename || '') : 'N/A',
        fmtPct(look.eyes_y),
        fmtPct(look.face_height),
        fmtPct(look.center_x),
        look.rotation ?? 0,
        hasRotation ? 'rotate_before_clip' : '',
        hasRotation ? 'place_at_angle' : '',
      ];

      // Photos
      for (let n = 0; n < maxPhotos; n++) {
        const photo = photos[n];
        if (photo && photo.look_ref) {
          row.push(
            photo.look_ref,
            'row_reference',
            'id',
            photo.fit_mode || '',
            fmtPct(photo.width),
            fmtPct(photo.height),
            fmtPct(photo.x),
            fmtPct(photo.y)
          );
        } else if (photo && photo.source_type === 'database' && photo.pose_name) {
          row.push(
            photo.pose_name,
            'row_reference',
            'id',
            photo.fit_mode || '',
            fmtPct(photo.width),
            fmtPct(photo.height),
            fmtPct(photo.x),
            fmtPct(photo.y)
          );
        } else {
          row.push('', '', '', '', '', '', '', '');
        }
      }

      // Overlay
      const hasOverlay = !!(look.overlay_s3_path && look.overlay_filename);
      row.push(
        hasOverlay ? look.overlay_s3_path : 'N/A',
        hasOverlay ? look.overlay_filename : 'N/A',
        '', // overlay_composite_operation
        ''  // overlay_affects_layers_below
      );

      // Text fields — convert pt values to px using canvas DPI (px = pt * dpi / 72)
      const dpi = look.canvas_dpi || 300;
      const ptToPx = (pt) => pt != null ? Math.round(pt * dpi / 72) : null;

      for (let n = 0; n < maxFields; n++) {
        const tf = fields[n];
        if (tf) {
          row.push(
            tf.content || '',
            tf.font_s3_path || '',
            tf.font_filename || '',
            tf.font_color || '',
            tf.font_size_min != null ? `${ptToPx(tf.font_size_min)}px` : '',
            tf.font_size_max != null ? `${ptToPx(tf.font_size_max)}px` : '',
            fmtPct(tf.center_x),
            fmtPct(tf.center_y),
            fmtPct(tf.bound_x_left),
            fmtPct(tf.bound_x_right),
            tf.sizing_rule || ''
          );
        } else {
          row.push('', '', '', '', '', '', '', '', '', '', '');
        }
      }

      return row;
    });

    // === GENERATE CSV STRING ===
    const escapeCsv = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.map(escapeCsv).join(','),
      ...rows.map(r => r.map(escapeCsv).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${season || 'favorites'}_favorites_spec.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasErrors = validation?.errors?.length > 0;
  const hasWarnings = validation?.warnings?.length > 0;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleGenerate}
        style={{
          padding: '6px 14px', background: '#fff', border: '1px solid #ccc',
          borderRadius: 4, cursor: 'pointer', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap'
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> Generate CSV
      </button>

      {showPanel && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 4,
          background: '#fff', border: '1px solid #ccc', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,.15)', padding: 16,
          zIndex: 200, width: 420, maxHeight: 400, overflow: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>CSV Export Validation</span>
            <button
              onClick={() => setShowPanel(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#999' }}
            >
              &times;
            </button>
          </div>

          {hasErrors && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#d32f2f', marginBottom: 4 }}>
                Errors ({validation.errors.length})
              </div>
              {validation.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 11, color: '#d32f2f', padding: '2px 0', paddingLeft: 8 }}>
                  {e}
                </div>
              ))}
            </div>
          )}

          {hasWarnings && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e65100', marginBottom: 4 }}>
                Warnings ({validation.warnings.length})
              </div>
              {validation.warnings.map((w, i) => (
                <div key={i} style={{ fontSize: 11, color: '#e65100', padding: '2px 0', paddingLeft: 8 }}>
                  {w}
                </div>
              ))}
            </div>
          )}

          {!hasErrors && !hasWarnings && (
            <div style={{ fontSize: 12, color: '#2e7d32', marginBottom: 12 }}>
              All looks pass validation.
            </div>
          )}

          <button
            onClick={generateCsv}
            disabled={hasErrors}
            style={{
              width: '100%', padding: '8px 16px',
              background: hasErrors ? '#ccc' : '#1976d2',
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: hasErrors ? 'default' : 'pointer',
              fontSize: 13, fontWeight: 500
            }}
          >
            {hasErrors
              ? 'Fix errors to download'
              : hasWarnings
                ? `Download CSV (${validation.warnings.length} warnings)`
                : 'Download CSV'}
          </button>

          <div style={{ fontSize: 10, color: '#999', marginTop: 8, textAlign: 'center' }}>
            {seasonLooks.length} look{seasonLooks.length !== 1 ? 's' : ''} for {season}
          </div>
        </div>
      )}
    </div>
  );
};

export default CsvExport;
